import 'leaflet/dist/leaflet.css';
import './styles.css';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { supabase } from './lib/supabase.js';
import { getMyProfile, getConfig } from './lib/api.js';
import { SUPABASE_URL, PLAY_STORE_URL, APP_VERSION } from './lib/config.js';
import { $, esc, setBanner } from './lib/ui.js';
import { renderLogin } from './views/login.js';
import { renderPlan } from './views/plan.js';
import { renderBoard } from './views/board.js';
import { renderLocations } from './views/locations.js';
import { renderMyRoute } from './views/myroute.js';
import { renderUsers } from './views/users.js';

const isNative = Capacitor.isNativePlatform();
const state = { session: null, profile: null, cleanup: null };

// Which screens each role gets, in tab order. First one is the home screen.
const ROUTES = {
  plan:      { label: 'Plan',      roles: ['admin', 'dispatcher'], render: renderPlan },
  board:     { label: 'Board',     roles: ['admin', 'dispatcher'], render: renderBoard },
  locations: { label: 'Locations', roles: ['admin', 'dispatcher'], render: renderLocations },
  route:     { label: 'My route',  roles: ['driver', 'admin', 'dispatcher'], render: renderMyRoute },
  users:     { label: 'Users',     roles: ['admin'], render: renderUsers },
};

function allowed(role) { return Object.entries(ROUTES).filter(([, r]) => r.roles.includes(role)); }

async function route() {
  if (state.cleanup) { try { state.cleanup(); } catch { /* ignore */ } state.cleanup = null; }
  const app = $('#app');
  app.innerHTML = '';
  if (!state.session) { $('#nav').innerHTML = ''; $('#who').innerHTML = ''; return renderLogin(app); }
  if (!state.profile?.active) {
    app.innerHTML = `<div class="center card"><p>Your account is not active yet. Ask the admin to enable it.</p><p class="hint">Signed in as ${esc(state.session.user.email)}</p><button id="so">Sign out</button></div>`;
    $('#so').onclick = () => supabase.auth.signOut();
    return;
  }
  const tabs = allowed(state.profile.role);
  const key = location.hash.replace(/^#\/?/, '') || tabs[0][0];
  const current = tabs.find(([k]) => k === key) ? key : tabs[0][0];
  $('#nav').innerHTML = tabs.map(([k, r]) => `<a href="#/${k}" class="${k === current ? 'on' : ''}">${r.label}</a>`).join('');
  $('#who').innerHTML = `${esc(state.profile.full_name || state.session.user.email)} · ${esc(state.profile.role)} <button id="signout">Sign out</button>`;
  $('#signout').onclick = () => supabase.auth.signOut();
  state.cleanup = await ROUTES[current].render(app, { profile: state.profile, session: state.session, isNative }) || null;
}

/** Blocks the Android app when it is older than android_min_version_code (forced update). */
async function versionGate() {
  let cfg = {};
  try { cfg = await getConfig(); } catch { return true; } // offline: let them in
  if (cfg.banner) setBanner(esc(cfg.banner));
  if (!isNative) return true;
  const info = await App.getInfo(); // info.build = Android versionCode
  const code = parseInt(info.build, 10) || 0;
  if (code < (+cfg.android_min_version_code || 0)) {
    $('#app').innerHTML = `<div class="center card"><h3>Update required</h3>
      <p style="margin:10px 0">This version (${esc(info.version)}) is no longer supported. Please update from the Play Store.</p>
      <a class="btn primary big" href="${esc(PLAY_STORE_URL)}">Open Play Store</a></div>`;
    return false;
  }
  if (code < (+cfg.android_latest_version_code || 0)) {
    setBanner(`A newer version is available. <a href="${esc(PLAY_STORE_URL)}">Update</a>`);
  }
  return true;
}

async function loadSession(session) {
  state.session = session;
  state.profile = session ? await getMyProfile(session.user.id).catch(() => null) : null;
  route();
}

async function boot() {
  if (!SUPABASE_URL) {
    $('#app').innerHTML = '<div class="err pad">This build is missing its configuration (VITE_SUPABASE_URL). See README.</div>';
    return;
  }
  if (!(await versionGate())) return;

  if (isNative) {
    App.addListener('backButton', ({ canGoBack }) => { if (canGoBack) history.back(); else App.exitApp(); });
    // Re-check forced update when the app comes back to the foreground.
    App.addListener('resume', () => versionGate());
  } else {
    registerServiceWorker();
  }

  const { data: { session } } = await supabase.auth.getSession();
  supabase.auth.onAuthStateChange((event, s) => {
    if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') loadSession(s);
    else state.session = s; // token refresh: keep the screen as is
  });
  window.addEventListener('hashchange', route);
  await loadSession(session);
}

/** Web/PWA only: offers a reload when a new version has been deployed. */
async function registerServiceWorker() {
  try {
    const { registerSW } = await import('virtual:pwa-register');
    const update = registerSW({
      onNeedRefresh() {
        setBanner('A new version of ACME Routes is available. <button id="reload">Reload</button>');
        $('#reload').onclick = () => update(true);
      },
    });
  } catch { /* dev server without SW */ }
}

console.info('ACME Routes', APP_VERSION);
boot();
